import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/api';
import { useEmpresaSelector } from '../../context/EmpresaSelectorContext';
import '../../styles/cierrecaja.css';

export default function CierreCaja() {
  const { empresaSeleccionada, esConexAdmin, empresas } = useEmpresaSelector();
  const [showAllEmpresasModal, setShowAllEmpresasModal] = useState(false);
  const pendingVentaRef = useRef(null);

  // Limpiar ingresos automáticos del localStorage al cargar
  const limpiarIngresosAutomaticos = (ventasGuardadas) => {
    return ventasGuardadas.filter(venta => !venta.esIngreso);
  };

  const [ventas, setVentas] = useState([]);
  const [itemsInventario, setItemsInventario] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [cuentasPorCobrar, setCuentasPorCobrar] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(true);
  const [nuevaVenta, setNuevaVenta] = useState({
    cliente: '',
    ciRuc: '',
    contacto: '', // nuevo campo
    producto: '',
    costo: '',
    cantidad: 1,
    iva: 0, // porcentaje 0-100
    total: '',
    pagado: '',
    tipoPago: 'efectivo',
    banco: '',
    comprobante: '',
    fecha: new Date().toISOString().split('T')[0],
    abono: '', // solo para tipoPago 'porPagar'
    numeroPago: '1', // solo para tipoPago 'porPagar'
  });
  const [editVentaId, setEditVentaId] = useState(null);
  const [errorVenta, setErrorVenta] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState(new Date().toISOString().split('T')[0]);

  // Resetear ventas cuando cambia la empresa seleccionada
  useEffect(() => {
    // Cargar ventas desde localStorage para la nueva empresa
    const ventasGuardadas = JSON.parse(localStorage.getItem(`cierreCaja_${empresaSeleccionada?.id || 'all'}`)) || [];
    const ventasLimpias = limpiarIngresosAutomaticos(ventasGuardadas);
    if (ventasLimpias.length !== ventasGuardadas.length) {
      localStorage.setItem(`cierreCaja_${empresaSeleccionada?.id || 'all'}`, JSON.stringify(ventasLimpias));
    }
    setVentas(ventasLimpias);
  }, [empresaSeleccionada?.id]);

  // Cargar ventas iniciales desde API: si el backend no tiene datos, conservar localStorage
  useEffect(() => {
    (async () => {
      try {
        const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
        const qs = empresaId ? `?empresaId=${empresaId}` : '';

        // Cargar cierres de caja desde backend para esta empresa
        const response = await apiRequest(`/cierres-caja${qs}`);
        if (response.ok) {
          const cierres = await response.json();
          // Filtrar solo los cierres que son ventas manuales (no cierres diarios)
          const ventasManuales = cierres.filter(c => c.tipoPago && c.producto && c.cantidad);

          const ventasLocales = JSON.parse(localStorage.getItem(`cierreCaja_${empresaSeleccionada?.id || 'all'}`)) || [];

          // Crear un mapa de ventas por ID para evitar duplicados
          const ventasMap = new Map();

          // Primero agregar todas las ventas locales (incluyendo las que tienen ID)
          ventasLocales.forEach(venta => {
            if (venta.id) {
              ventasMap.set(venta.id, venta);
            }
          });

          // Luego agregar/actualizar con las ventas del backend
          ventasManuales.forEach(venta => {
            if (venta.id) {
              ventasMap.set(venta.id, venta);
            }
          });

          // Agregar ventas locales sin ID (nuevas ventas no guardadas aún)
          ventasLocales.filter(v => !v.id).forEach(venta => {
            ventasMap.set(`local_${Date.now()}_${Math.random()}`, venta);
          });

          // Convertir el mapa a array
          const ventasCombinadas = Array.from(ventasMap.values());

          // Solo actualizar si hay cambios
          if (ventasCombinadas.length !== ventas.length ||
              JSON.stringify(ventasCombinadas) !== JSON.stringify(ventas)) {
            setVentas(ventasCombinadas);
          }
        }
      } catch (_) {
        // Silenciar, se mantiene localStorage
      }
    })();
  }, [empresaSeleccionada?.id, esConexAdmin]);

  // Cargar productos del inventario desde el backend
  useEffect(() => {
    const cargarProductosInventario = async () => {
      if (!empresaSeleccionada || !empresaSeleccionada.id) {
        setLoadingProductos(false);
        return;
      }

      setLoadingProductos(true);
      try {
        let endpoint = '/inventario';
        if (empresaSeleccionada.id !== 'all') {
          endpoint += `?empresaId=${empresaSeleccionada.id}`;
        }

        const response = await apiRequest(endpoint);
        if (response.ok) {
          const data = await response.json();
          setItemsInventario(Array.isArray(data) ? data : []);
        } else {
          console.error('Error cargando productos del inventario:', response.status, response.statusText);
          setItemsInventario([]);
        }
      } catch (error) {
        console.error('Error cargando productos del inventario:', error);
        setItemsInventario([]);
      } finally {
        setLoadingProductos(false);
      }
    };

    cargarProductosInventario();
  }, [empresaSeleccionada?.id]);

  // Cargar cuentas por cobrar desde el backend
  useEffect(() => {
    const cargarCuentasPorCobrar = async () => {
      if (!empresaSeleccionada || !empresaSeleccionada.id) {
        setLoadingCuentas(false);
        return;
      }

      setLoadingCuentas(true);
      try {
        let endpoint = '/cuentas-por-cobrar';
        if (empresaSeleccionada.id !== 'all') {
          endpoint += `?empresaId=${empresaSeleccionada.id}`;
        }

        const response = await apiRequest(endpoint);
        if (response.ok) {
          const data = await response.json();
          setCuentasPorCobrar(Array.isArray(data) ? data : []);
        } else {
          console.error('Error cargando cuentas por cobrar:', response.status, response.statusText);
          setCuentasPorCobrar([]);
        }
      } catch (error) {
        console.error('Error cargando cuentas por cobrar:', error);
        setCuentasPorCobrar([]);
      } finally {
        setLoadingCuentas(false);
      }
    };

    cargarCuentasPorCobrar();
  }, [empresaSeleccionada?.id]);


  useEffect(() => {
    // Guardar todas las ventas en localStorage para evitar pérdida de datos
    localStorage.setItem(`cierreCaja_${empresaSeleccionada?.id || 'all'}`, JSON.stringify(ventas));
  }, [ventas, empresaSeleccionada?.id]);

  // Validar y agregar/editar venta
  const handleVentaSubmit = async () => {
    if (!nuevaVenta.producto || !nuevaVenta.costo || !nuevaVenta.fecha) {
      setErrorVenta('Producto, costo y fecha son obligatorios');
      return;
    }
    if (nuevaVenta.tipoPago === 'porPagar' && !nuevaVenta.fecha) {
      setErrorVenta('La fecha es obligatoria');
      return;
    }
    if (isNaN(nuevaVenta.costo) || nuevaVenta.costo < 0) {
      setErrorVenta('Costo debe ser un número válido');
      return;
    }
    const cantidad = nuevaVenta.tipoPago === 'porPagar' ? parseInt(nuevaVenta.cantidad || 1) : parseFloat(nuevaVenta.cantidad || 1);
    if (isNaN(cantidad) || cantidad <= 0) {
      setErrorVenta(nuevaVenta.tipoPago === 'porPagar' ? 'Debe seleccionar un número de pago válido' : 'Cantidad debe ser mayor a 0');
      return;
    }
    if (nuevaVenta.tipoPago === 'transferencia' && !nuevaVenta.banco) {
      setErrorVenta('Debe especificar un banco para transferencia');
      return;
    }
    if (nuevaVenta.tipoPago === 'porPagar' && !nuevaVenta.cliente) {
      setErrorVenta('Debe ingresar el nombre del cliente');
      return;
    }
    if (nuevaVenta.tipoPago === 'porPagar' && (!nuevaVenta.abono || parseFloat(nuevaVenta.abono) < 0)) {
      setErrorVenta('Debe ingresar un monto de abono válido (0 o mayor)');
      return;
    }

    const costo = parseFloat(nuevaVenta.costo) || 0;
    const iva = parseFloat(nuevaVenta.iva || 0) / 100; // porcentaje
    const subtotal = nuevaVenta.tipoPago === 'porPagar' ? 0 : costo * cantidad; // Para porPagar, el subtotal es 0 ya que el monto viene del pago
    const total = nuevaVenta.tipoPago === 'porPagar' ? 0 : subtotal * (1 + iva);
    // Para porPagar, pagado debe ser el adeudado (total - abono)
    const pagado = nuevaVenta.tipoPago === 'porPagar'
      ? (subtotal * (1 + iva)) - parseFloat(nuevaVenta.abono || 0)
      : parseFloat(nuevaVenta.pagado || total);

    const venta = {
      ...nuevaVenta,
      costo,
      cantidad,
      iva: parseFloat(nuevaVenta.iva || 0),
      total,
      pagado,
    };

    // Validar stock suficiente para TODOS los tipos de pago (incluyendo porPagar)
    const productoInventario = itemsInventario.find(it => it.nombre === nuevaVenta.producto);
    if (productoInventario && productoInventario.id) {
      let stockFinalProyectado = 0;
      if (editVentaId !== null) {
        const ventaOriginal = ventas[editVentaId];
        if (ventaOriginal && ventaOriginal.producto === nuevaVenta.producto) {
          // Mismo producto
          const cantidadOriginal = parseFloat(ventaOriginal.cantidad || 1);
          const nuevaSalida = (productoInventario.salida || 0) + (cantidad - cantidadOriginal);
          stockFinalProyectado = productoInventario.cantidadEnStock - nuevaSalida;
        } else {
          // Cambió producto, verificar solo el nuevo
          stockFinalProyectado = productoInventario.cantidadEnStock - ((productoInventario.salida || 0) + cantidad);
        }
      } else {
        // Nueva venta
        stockFinalProyectado = productoInventario.cantidadEnStock - ((productoInventario.salida || 0) + cantidad);
      }
      if (stockFinalProyectado < 0) {
        setErrorVenta('No hay suficiente inventario para esta venta. Stock disponible: ' + (productoInventario.cantidadEnStock - (productoInventario.salida || 0)));
        return;
      }
    }

    // Si está en "Todas las empresas" y NO está editando, mostrar modal de confirmación
    if (esConexAdmin && empresaSeleccionada?.id === 'all' && editVentaId === null) {
      pendingVentaRef.current = venta;
      setShowAllEmpresasModal(true);
      return;
    }

    try {
      if (editVentaId !== null) {
        const actual = ventas[editVentaId] || {};
        // Es una venta manual existente, intentar actualizar
        if (actual.id) {

          // Para edición, recalcular pagado correctamente para porPagar
          const ventaActualizada = { ...venta };
          if (venta.tipoPago === 'porPagar') {
            const costo = parseFloat(venta.costo) || 0;
            const cantidad = parseFloat(venta.cantidad) || 1;
            const iva = parseFloat(venta.iva || 0) / 100;
            const abono = parseFloat(venta.abono || 0);
            const subtotal = costo * cantidad;
            const totalConIva = subtotal * (1 + iva);
            ventaActualizada.pagado = Math.max(0, totalConIva - abono);
          }

          const res = await apiRequest(`/cierres-caja/${actual.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              ...ventaActualizada,
              empresaId: empresaSeleccionada?.id || actual.empresaId || null,
              usuarioId: actual.usuarioId || 1,
              fecha: venta.fecha // asegurar que se envía la fecha
            }),
          });
          if (res.ok) {
            const actualizado = await res.json().catch(() => ({ ...venta, id: actual.id }));

            const nuevasVentas = ventas.map((v, i) => (i === editVentaId ? { ...actualizado } : v));
            setVentas(nuevasVentas);
          } else {
            const errorText = await res.text().catch(() => 'Error desconocido');
            console.error('Error al actualizar venta:', res.status, errorText);
            // Fallback local
            const nuevasVentas = ventas.map((v, i) => (i === editVentaId ? { ...venta, id: actual.id, empresaId: empresaSeleccionada?.id || actual.empresaId || null } : v));
            setVentas(nuevasVentas);
          }
        } else {
          // Sin id backend, actualizar localmente
          const nuevasVentas = ventas.map((v, i) => (i === editVentaId ? { ...venta, id: actual.id, empresaId: empresaSeleccionada?.id || actual.empresaId || null } : v));
          setVentas(nuevasVentas);
        }
        setEditVentaId(null);
      } else {
        // Crear en backend primero para obtener id real

        const res = await apiRequest('/cierres-caja', {
          method: 'POST',
          body: JSON.stringify({
            ...venta,
            empresaId: empresaSeleccionada?.id || null,
            usuarioId: 1, // conexadmin por defecto
            fecha: venta.fecha // asegurar que se envía la fecha
          }),
        });
        if (res.ok) {
          const creado = await res.json().catch(() => null);

          if (creado && creado.id) {
            setVentas([...ventas, creado]);
          } else {
            setVentas([...ventas, { ...venta, id: ventas.length + 1, empresaId: empresaSeleccionada?.id || null }]);
          }
        } else {
          const errorText = await res.text().catch(() => 'Error desconocido');
          console.error('Error al crear venta:', res.status, errorText);
          // Fallback local si falla backend
          setVentas([...ventas, { ...venta, id: ventas.length + 1, empresaId: empresaSeleccionada?.id || null }]);
        }
      }
    } catch (err) {
      // Fallback local si hay error inesperado
      setVentas([...ventas, { ...venta, id: ventas.length + 1, empresaId: empresaSeleccionada?.id || null }]);
    }

    // Ajustar salida del inventario para TODOS los tipos de pago (incluyendo porPagar)
    if (productoInventario && productoInventario.id) {
      try {
        if (editVentaId !== null) {
          // Si estamos editando
          const ventaOriginal = ventas[editVentaId];
          if (ventaOriginal && ventaOriginal.producto === nuevaVenta.producto) {
            // Mismo producto, ajustar diferencia
            const cantidadOriginal = parseFloat(ventaOriginal.cantidad || 1);
            const ajuste = cantidad - cantidadOriginal;
            const nuevoSalida = Math.max((productoInventario.salida || 0) + ajuste, 0);
            await apiRequest(`/inventario/${productoInventario.id}`, {
              method: 'PATCH',
              body: JSON.stringify({
                salida: nuevoSalida
              })
            });
            setItemsInventario(prev => prev.map(it =>
              it.id === productoInventario.id
                ? { ...it, salida: nuevoSalida }
                : it
            ));
          } else {
            // Cambió el producto, sumar cantidad original a salida del original, sumar cantidad a salida del nuevo
            if (ventaOriginal && ventaOriginal.producto) {
              const productoOriginal = itemsInventario.find(it => it.nombre === ventaOriginal.producto);
              if (productoOriginal) {
                const cantidadOriginal = parseFloat(ventaOriginal.cantidad || 1);
                const nuevoSalidaOriginal = (productoOriginal.salida || 0) + cantidadOriginal;
                await apiRequest(`/inventario/${productoOriginal.id}`, {
                  method: 'PATCH',
                  body: JSON.stringify({
                    salida: nuevoSalidaOriginal
                  })
                });
                setItemsInventario(prev => prev.map(it =>
                  it.id === productoOriginal.id
                    ? { ...it, salida: nuevoSalidaOriginal }
                    : it
                ));
              }
            }
            // Para el nuevo producto
            const nuevoSalidaNuevo = (productoInventario.salida || 0) + cantidad;
            await apiRequest(`/inventario/${productoInventario.id}`, {
              method: 'PATCH',
              body: JSON.stringify({
                salida: nuevoSalidaNuevo
              })
            });
            setItemsInventario(prev => prev.map(it =>
              it.id === productoInventario.id
                ? { ...it, salida: nuevoSalidaNuevo }
                : it
            ));
          }
        } else {
          // Nueva venta, sumar cantidad a salida
          const nuevoSalida = (productoInventario.salida || 0) + cantidad;
          await apiRequest(`/inventario/${productoInventario.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              salida: nuevoSalida
            })
          });
          setItemsInventario(prev => prev.map(it =>
            it.id === productoInventario.id
              ? { ...it, salida: nuevoSalida }
              : it
          ));
        }
      } catch (error) {
        console.error('Error actualizando salida del inventario:', error);
        // No fallar el proceso completo por error en inventario
      }
    }

    setNuevaVenta({
      cliente: '',
      ciRuc: '',
      contacto: '',
      producto: '',
      costo: '',
      cantidad: 1,
      iva: 0,
      total: '',
      pagado: '',
      tipoPago: 'efectivo',
      banco: '',
      comprobante: '',
      fecha: new Date().toISOString().split('T')[0],
      abono: '',
      numeroPago: '1'
    });
    setErrorVenta('');
  };

  // Crear venta para todas las empresas (cuando se confirma en el modal)
  const handleCrearParaTodasEmpresas = async () => {
    const venta = pendingVentaRef.current;
    if (!venta || !empresas || empresas.length === 0) {
      setShowAllEmpresasModal(false);
      return;
    }

    try {
      for (const empresa of empresas) {

        const res = await apiRequest('/cierres-caja', {
          method: 'POST',
          body: JSON.stringify({
            ...venta,
            empresaId: empresa.id,
            usuarioId: 1,
            fecha: venta.fecha
          }),
        });
        if (res.ok) {
          const creado = await res.json().catch(() => null);

        } else {
          console.error('Error creando venta para empresa', empresa.nombre);
        }
      }

      // Recargar datos de ventas desde backend
      const qs = '';
      const response = await apiRequest(`/cierres-caja${qs}`);
      if (response.ok) {
        const cierres = await response.json();
        const ventasManuales = cierres.filter(c => c.tipoPago && c.producto && c.cantidad);
        setVentas(ventasManuales);
      }

      // Limpiar formulario
      setNuevaVenta({
        cliente: '',
        ciRuc: '',
        contacto: '',
        producto: '',
        costo: '',
        cantidad: 1,
        iva: 0,
        total: '',
        pagado: '',
        tipoPago: 'efectivo',
        banco: '',
        comprobante: '',
        fecha: new Date().toISOString().split('T')[0],
        abono: '',
        numeroPago: '1'
      });
      setErrorVenta('');
    } catch (err) {
      console.error('Error creando ventas para todas las empresas:', err);
      setErrorVenta('Error al crear ventas para todas las empresas');
    } finally {
      setShowAllEmpresasModal(false);
      pendingVentaRef.current = null;
    }
  };

  // Editar venta
  const handleEditVenta = (index) => {
    const venta = ventas[index];
    setEditVentaId(index);
    // Asegurar que todos los campos estén presentes, especialmente los nuevos
    setNuevaVenta({
      cliente: venta.cliente || '',
      ciRuc: venta.ciRuc || '',
      contacto: venta.contacto || '',
      producto: venta.producto || '',
      costo: venta.costo || '',
      cantidad: venta.cantidad || 1,
      iva: venta.iva || 0,
      total: venta.total || '',
      pagado: venta.pagado || '',
      tipoPago: venta.tipoPago || 'efectivo',
      banco: venta.banco || '',
      comprobante: venta.comprobante || '',
      fecha: venta.fecha || new Date().toISOString().split('T')[0],
      abono: venta.abono || '',
      numeroPago: venta.numeroPago || '1'
    });
  };

  // Eliminar venta
  const handleDeleteVenta = async (index) => {
    const actual = ventas[index];

    // Si la venta tiene un producto, restaurar la salida en inventario (para TODOS los tipos de pago)
    if (actual.producto) {
      const productoInventario = itemsInventario.find(it => it.nombre === actual.producto);
      if (productoInventario && productoInventario.id) {
        try {
          const cantidadADevolver = parseFloat(actual.cantidad || 1);
          const nuevoSalida = Math.max((productoInventario.salida || 0) - cantidadADevolver, 0);
          await apiRequest(`/inventario/${productoInventario.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              salida: nuevoSalida
            })
          });
          // Actualizar localmente
          setItemsInventario(prev => prev.map(it =>
            it.id === productoInventario.id
              ? { ...it, salida: nuevoSalida }
              : it
          ));
        } catch (error) {
          console.error('Error restaurando salida del inventario:', error);
        }
      }
    }

    try {
      if (actual?.id) {

        const res = await apiRequest(`/cierres-caja/${actual.id}`, { method: 'DELETE' });
        if (res.ok) {

        } else {
          const errorText = await res.text().catch(() => 'Error desconocido');
          console.error('Error al eliminar venta del backend:', res.status, errorText);
        }
      }
    } catch (error) {
      console.error('Error eliminando venta:', error);
    }
    // Eliminar localmente inmediatamente
    const nuevasVentas = ventas.filter((_, i) => i !== index);
    setVentas(nuevasVentas);

    // Si es un pago por cobrar, podríamos necesitar actualizar la cuenta por cobrar aquí
    // pero por ahora solo eliminamos la entrada de ingreso diario
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setEditVentaId(null);
    setNuevaVenta({
      cliente: '',
      ciRuc: '',
      contacto: '',
      producto: '',
      costo: '',
      cantidad: 1,
      iva: 0,
      total: '',
      pagado: '',
      tipoPago: 'efectivo',
      banco: '',
      comprobante: '',
      fecha: new Date().toISOString().split('T')[0],
      abono: '',
      numeroPago: '1'
    });
    setErrorVenta('');
  };

  // Función auxiliar para comparar fechas ignorando la hora y zona horaria
  const esMismoDia = (fecha1, fecha2) => {
    if (!fecha1 || !fecha2) return false;
    
    // Convertir ambas fechas a cadenas de fecha en formato YYYY-MM-DD para comparación
    const toLocalDateString = (dateStr) => {
      const date = new Date(dateStr);
      // Ajustar la fecha sumando la diferencia de la zona horaria
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
      return adjustedDate.toISOString().split('T')[0];
    };
    
    const fecha1Str = toLocalDateString(fecha1);
    const fecha2Str = toLocalDateString(fecha2);
    
    return fecha1Str === fecha2Str;
  };

  // Calcular suma total del día seleccionado (o todos si no hay filtro)
  // Total pagado = Abono (de porPagar) + Pagado (de ventas normales)
  const calcularTotalDia = () => {
    const ventasFiltradas = fechaFiltro
      ? ventas.filter((venta) => venta.fecha && esMismoDia(venta.fecha, fechaFiltro))
      : ventas;



    const totalAbonos = ventasFiltradas
      .filter(venta => venta.tipoPago === 'porPagar')
      .reduce((total, venta) => total + parseFloat(venta.abono || 0), 0);

    const totalPagadoNormal = ventasFiltradas
      .filter(venta => venta.tipoPago !== 'porPagar')
      .reduce((total, venta) => total + parseFloat(venta.pagado || 0), 0);

    return (totalAbonos + totalPagadoNormal).toFixed(2);
  };

  // Calcular total por tipo de pago del día (o todos si no hay filtro)
  const calcularTotalPorTipoPago = (tipo) => {
    const ventasFiltradas = fechaFiltro
      ? ventas.filter((venta) => venta.fecha && esMismoDia(venta.fecha, fechaFiltro))
      : ventas;

    if (tipo === 'porPagar') {
      // Para porPagar: mostrar el adeudado total (no el pagado)
      return ventasFiltradas
        .filter(venta => venta.tipoPago === 'porPagar')
        .reduce((total, venta) => {
          const costo = parseFloat(venta.costo || 0);
          const cantidad = parseFloat(venta.cantidad || 1);
          const iva = parseFloat(venta.iva || 0) / 100;
          const totalCostoIva = (costo * cantidad) * (1 + iva);
          const abono = parseFloat(venta.abono || 0);
          const adeudado = totalCostoIva - abono;
          return total + adeudado;
        }, 0)
        .toFixed(2);
    } else {
      // Para efectivo y transferencia: mostrar el pagado
      return ventasFiltradas
        .filter(venta => (venta.tipoPago || 'efectivo') === tipo)
        .reduce((total, venta) => total + parseFloat(venta.pagado || 0), 0)
        .toFixed(2);
    }
  };

  // Calcular total abonado del día (o todos si no hay filtro)
  const calcularTotalAbonado = () => {
    const ventasFiltradas = fechaFiltro
      ? ventas.filter((venta) => venta.fecha && esMismoDia(venta.fecha, fechaFiltro))
      : ventas;
    return ventasFiltradas
      .filter(venta => venta.tipoPago === 'porPagar')
      .reduce((total, venta) => total + parseFloat(venta.abono || 0), 0)
      .toFixed(2);
  };

  // Obtener resultados históricos por día
  const resultadosHistoricos = () => {
    const porDia = {};
    ventas.forEach((venta) => {
      if (!porDia[venta.fecha]) {
        porDia[venta.fecha] = { total: 0, cantidad: 0 };
      }
      porDia[venta.fecha].total += parseFloat(venta.pagado);
      porDia[venta.fecha].cantidad += 1;
    });
    return Object.entries(porDia).map(([fecha, datos]) => ({
      fecha,
      total: datos.total.toFixed(2),
      cantidad: datos.cantidad,
    }));
  };

  // Descargar tabla del día como Excel
  const descargarExcelDia = async () => {
    // Importar XLSX dinámicamente solo cuando se necesite
    const XLSX = await import('xlsx');

    const ventasDia = fechaFiltro
      ? ventas.filter((venta) => venta.fecha && esMismoDia(venta.fecha, fechaFiltro))
      : ventas;



    const worksheetData = ventasDia.map((venta) => ({
      Cliente: venta.cliente || '',
      'CI/RUC': venta.ciRuc || '',
      Contacto: venta.contacto || '',
      Producto: venta.producto || (venta.tipoPago === 'porPagar' ? 'Pago de Cuenta por Cobrar' : ''),
      Costo: venta.tipoPago === 'porPagar' ? `Abono: $${parseFloat(venta.abono || 0).toFixed(2)}` : venta.costo,
      Cantidad: venta.tipoPago === 'porPagar' ? `Pago ${venta.numeroPago || venta.cantidad}` : venta.cantidad,
      IVA: `${venta.iva || 0}%`,
      Total: venta.total,
      Pagado: venta.pagado,
      'Tipo de Pago': venta.tipoPago === 'efectivo' ? 'Efectivo' : venta.tipoPago === 'transferencia' ? 'Transferencia' : 'Por Cobrar',
      Banco: venta.banco || '',
      Comprobante: venta.comprobante || '',
      Fecha: venta.fecha,
    }));

    // Filtrar cuentas por cobrar por fecha (usar fechaVencimiento o createdAt)
    const cuentasPorCobrarDia = fechaFiltro
      ? cuentasPorCobrar.filter((cuenta) => {
          const fechaCuenta = cuenta.fechaVencimiento || cuenta.createdAt;
          return fechaCuenta && esMismoDia(fechaCuenta, fechaFiltro);
        })
      : cuentasPorCobrar;

    const cuentasWorksheetData = cuentasPorCobrarDia.map((cuenta) => ({
      Cliente: cuenta.cliente || '',
      'CI/RUC': cuenta.ciRuc || '',
      'Descripción': cuenta.descripcion || '',
      'Monto Total': parseFloat(cuenta.montoTotal || 0).toFixed(2),
      'Saldo Adeudado': parseFloat(cuenta.saldoAdeudado || 0).toFixed(2),
      'Fecha Vencimiento': cuenta.fechaVencimiento ? new Date(cuenta.fechaVencimiento).toLocaleDateString('es-ES') : '',
      'Fecha Creación': cuenta.createdAt ? new Date(cuenta.createdAt).toLocaleDateString('es-ES') : '',
      'Estado': cuenta.estado || 'Pendiente',
      'Empresa': cuenta.empresaId || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const cuentasWorksheet = XLSX.utils.json_to_sheet(cuentasWorksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas del Día');
    XLSX.utils.book_append_sheet(workbook, cuentasWorksheet, 'Cuentas por Pagar');
    const fileName = fechaFiltro ? `cierre_caja_${fechaFiltro}.xlsx` : `cierre_caja_todos.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Descargar resumen mensual como Excel
  const descargarExcelMes = async () => {
    // Importar XLSX dinámicamente solo cuando se necesite
    const XLSX = await import('xlsx');

    const mes = fechaFiltro.slice(0, 7); // Ej: "2025-07"
    const ventasMes = resultadosHistoricos().filter((res) => res.fecha.startsWith(mes));
    const worksheetData = ventasMes.map((res) => ({
      Fecha: res.fecha,
      'Total Pagado': res.total,
      'Cantidad de Ventas': res.cantidad,
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumen Mensual');
    XLSX.writeFile(workbook, `cierre_caja_${mes}.xlsx`);
  };

  return (
    <section className="section has-background-light" style={{ paddingTop: '80px', minHeight: 'calc(100vh - 80px)' }}>
      <div className="container" style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '20px',
        overflowX: 'auto'
      }}>
        <h1 className="title has-text-warning has-text-centered">Ingresos Diarios</h1>

        {/* FORMULARIO PARA VENTAS */}
        <div className="box">
          <h2 className="subtitle has-text-centered has-text-black">{editVentaId !== null ? 'Editar Venta' : 'Agregar Venta'}</h2>
          {errorVenta && <p className="notification is-danger">{errorVenta}</p>}
          <div className="columns is-multiline">
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Cliente</label>
              <input
                className="input"
                type="text"
                placeholder="Cliente"
                value={nuevaVenta.cliente}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, cliente: e.target.value })}
              />
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">CI / RUC</label>
              <input
                className="input"
                type="text"
                placeholder="CI / RUC"
                value={nuevaVenta.ciRuc}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, ciRuc: e.target.value })}
              />
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Contacto</label>
              <input
                className="input"
                type="text"
                placeholder="Teléfono / Email"
                value={nuevaVenta.contacto}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, contacto: e.target.value })}
              />
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Producto</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaVenta.producto}
                  onChange={(e) => {
                    const nombre = e.target.value;
                    const it = itemsInventario.find(i => i.nombre === nombre);
                    setNuevaVenta({
                      ...nuevaVenta,
                      producto: nombre,
                      costo: it ? it.costoArticulo : nuevaVenta.costo,
                    });
                  }}
                  disabled={loadingProductos || !itemsInventario || itemsInventario.length === 0}
                >
                  <option value="">
                    {loadingProductos ? 'Cargando productos...' : itemsInventario && itemsInventario.length > 0 ? 'Seleccionar producto' : 'No hay productos disponibles'}
                  </option>
                  {itemsInventario && itemsInventario.length > 0 && itemsInventario.map((it) => (
                    <option key={it.id} value={it.nombre}>{it.nombre}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Costo</label>
              <input
                className="input"
                type="number"
                step="0.01"
                placeholder="Costo"
                value={nuevaVenta.costo}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, costo: e.target.value })}
              />
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Cantidad</label>
              <input
                className="input"
                type="number"
                step="1"
                placeholder="Cantidad"
                value={nuevaVenta.cantidad}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, cantidad: e.target.value })}
              />
            </div>
            {nuevaVenta.tipoPago === 'porPagar' && (
              <>
                <div className="column is-one-quarter">
                  <label className="label has-text-black is-small">Número de Pago</label>
                  <div className="select is-fullwidth">
                    <select
                      value={nuevaVenta.numeroPago || '1'}
                      onChange={(e) => setNuevaVenta({ ...nuevaVenta, numeroPago: e.target.value })}
                      disabled={!nuevaVenta.cliente}
                    >
                      <option value="1">Pago 1</option>
                      <option value="2">Pago 2</option>
                      <option value="3">Pago 3</option>
                      <option value="4">Pago 4</option>
                      <option value="5">Pago 5</option>
                      <option value="6">Pago 6</option>
                    </select>
                  </div>
                </div>
                <div className="column is-one-quarter">
                  <label className="label has-text-black is-small">Abono</label>
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    placeholder="Monto del abono"
                    value={nuevaVenta.abono}
                    onChange={(e) => {
                      setNuevaVenta({
                        ...nuevaVenta,
                        abono: e.target.value
                      });
                    }}
                  />
                </div>
              </>
            )}
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">IVA (%)</label>
              <div className="field has-addons">
                <p className="control is-expanded">
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    placeholder="IVA %"
                    value={nuevaVenta.iva}
                    onChange={(e) => setNuevaVenta({ ...nuevaVenta, iva: e.target.value })}
                  />
                </p>
                <p className="control">
                  <a className="button is-static">%</a>
                </p>
              </div>
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Total costo IVA</label>
              <input
                className="input"
                type="number"
                step="0.01"
                placeholder="Total costo IVA"
                value={(() => {
                  if (nuevaVenta.tipoPago === 'porPagar') {
                    const costo = parseFloat(nuevaVenta.costo || 0);
                    const cantidad = parseFloat(nuevaVenta.cantidad || 1);
                    const iva = parseFloat(nuevaVenta.iva || 0) / 100;
                    const totalCostoIva = (costo * cantidad) * (1 + iva);
                    return isNaN(totalCostoIva) ? '' : totalCostoIva.toFixed(2);
                  } else {
                    const c = parseFloat(nuevaVenta.costo || 0);
                    const cant = parseFloat(nuevaVenta.cantidad || 1);
                    const iva = parseFloat(nuevaVenta.iva || 0) / 100;
                    const total = (c * cant) * (1 + iva);
                    return isNaN(total) ? '' : total.toFixed(2);
                  }
                })()}
                readOnly
              />
            </div>
            {nuevaVenta.tipoPago === 'porPagar' && (
              <div className="column is-one-quarter">
                <label className="label has-text-black is-small">Adeudado</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder="Adeudado"
                  value={(() => {
                    const costo = parseFloat(nuevaVenta.costo || 0);
                    const cantidad = parseFloat(nuevaVenta.cantidad || 1);
                    const iva = parseFloat(nuevaVenta.iva || 0) / 100;
                    const totalCostoIva = (costo * cantidad) * (1 + iva);
                    const abono = parseFloat(nuevaVenta.abono || 0);
                    const total = totalCostoIva - abono;
                    return isNaN(total) ? '' : total.toFixed(2);
                  })()}
                  readOnly
                />
              </div>
            )}
            {nuevaVenta.tipoPago !== 'porPagar' && (
              <div className="column is-one-quarter">
                <label className="label has-text-black is-small">Pagado</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder="Pagado"
                  value={nuevaVenta.pagado}
                  onChange={(e) => setNuevaVenta({ ...nuevaVenta, pagado: e.target.value })}
                />
              </div>
            )}
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Efectivo / Transferencia / Por Cobrar</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaVenta.tipoPago}
                  onChange={(e) => setNuevaVenta({
                    ...nuevaVenta,
                    tipoPago: e.target.value,
                    banco: e.target.value === 'efectivo' ? '' : nuevaVenta.banco,
                    cliente: e.target.value === 'porPagar' ? nuevaVenta.cliente : (e.target.value === 'porPagar' ? '' : nuevaVenta.cliente)
                  })}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="porPagar">Por Cobrar</option>
                </select>
              </div>
            </div>
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small">Fecha</label>
              <input
                className="input"
                type="date"
                value={nuevaVenta.fecha}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, fecha: e.target.value })}
              />
            </div>
            {nuevaVenta.tipoPago === 'transferencia' && (
              <div className="column is-one-quarter">
                <label className="label has-text-black is-small">Banco</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Banco"
                  value={nuevaVenta.banco}
                  onChange={(e) => setNuevaVenta({ ...nuevaVenta, banco: e.target.value })}
                />
              </div>
            )}
            <div className="column is-one-quarter">
              <label className="label has-text-black is-small"># Comprobante</label>
              <input
                className="input"
                type="text"
                placeholder="# Comprobante"
                value={nuevaVenta.comprobante}
                onChange={(e) => setNuevaVenta({ ...nuevaVenta, comprobante: e.target.value })}
              />
            </div>
            <div className="column is-full">
              <div className="buttons">
                <button
                  className="button is-gold"
                  onClick={handleVentaSubmit}
                >
                  {editVentaId !== null ? 'Actualizar Venta' : 'Agregar Venta'}
                </button>
                {editVentaId !== null && (
                  <button
                    className="button is-light"
                    onClick={handleCancelEdit}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLA DE VENTAS E INGRESOS */}
        <div className="table-container mb-6">
          <h2 className="subtitle has-text-centered has-text-black">
            Ventas e Ingresos {fechaFiltro ? `del Día (${fechaFiltro})` : '(Todos)'}
          </h2>

          {/* Filtro de fecha al inicio */}
          <div className="field mb-4">
            <label className="label">Filtrar por fecha (opcional):</label>
            <div className="control is-flex is-align-items-center is-flex-wrap-wrap" style={{ gap: '10px' }}>
              <input
                className="input"
                type="date"
                value={fechaFiltro}
                onChange={(e) => setFechaFiltro(e.target.value || new Date().toISOString().split('T')[0])}
                style={{ maxWidth: '300px' }}
              />
              {fechaFiltro !== new Date().toISOString().split('T')[0] && (
                <button
                  className="button is-light is-small"
                  onClick={() => setFechaFiltro(new Date().toISOString().split('T')[0])}
                >
                  Volver a hoy
                </button>
              )}
              <button
                className="button is-gold is-small"
                onClick={descargarExcelDia}
                disabled={!fechaFiltro && ventas.length === 0}
              >
                <span className="icon">
                  <i className="fas fa-download"></i>
                </span>
                <span>Descargar Excel {fechaFiltro ? `(${fechaFiltro})` : '(Todos)'}</span>
              </button>
            </div>
          </div>

          {/* Tabla separada para Por Cobrar */}
          <div className="box mt-4" style={{ background: '#fff3cd' }}>
            <h3 className="subtitle is-6 has-text-centered has-text-dark">
              📋 Pagos por Cobrar Registrados
            </h3>
            {ventas.filter(venta => venta.tipoPago === 'porPagar').length === 0 ? (
              <p className="has-text-centered is-italic">No hay pagos por cobrar registrados</p>
            ) : (
              <div style={{
                width: '100%',
                overflowX: 'scroll',
                border: '1px solid #dbdbdb',
                borderRadius: '4px',
                padding: '10px',
                backgroundColor: 'white',
                scrollbarWidth: 'auto',
                WebkitOverflowScrolling: 'touch',
                minHeight: '150px',
                margin: '0 auto'
              }}>
                <table className="table is-fullwidth is-striped" style={{
                  minWidth: '1000px',
                  width: 'max-content',
                  marginBottom: '0'
                }}>
                  <thead>
                    <tr className="has-background-warning-light">
                      <th>Cliente</th>
                      <th>Contacto</th>
                      <th>Producto</th>
                      <th>Costo</th>
                      <th>Cantidad</th>
                      <th>Número de Pago</th>
                      <th>Abono</th>
                      <th>IVA (%)</th>
                      <th>Total Costo IVA</th>
                      <th>Adeudado</th>
                      <th>Fecha</th>
                      <th>Comprobante</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventas
                      .filter(venta => venta.tipoPago === 'porPagar')
                      .filter((venta) => !fechaFiltro || (venta.fecha && esMismoDia(venta.fecha, fechaFiltro)))
                      .map((venta, i) => (
                        <tr key={`porPagar-${venta.id || i}`}>
                              <td>{venta.cliente || '-'}</td>
                              <td>{venta.contacto || '-'}</td>
                              <td>{venta.producto || '-'}</td>
                              <td>${parseFloat(venta.costo || 0).toFixed(2)}</td>
                              <td>{venta.cantidad || 1}</td>
                              <td>Pago {venta.numeroPago || 1}</td>
                              <td>${parseFloat(venta.abono || 0).toFixed(2)}</td>
                              <td>{venta.iva || 0}%</td>
                              <td>${(() => {
                                const costo = parseFloat(venta.costo || 0);
                                const cantidad = parseFloat(venta.cantidad || 1);
                                const iva = parseFloat(venta.iva || 0) / 100;
                                return ((costo * cantidad) * (1 + iva)).toFixed(2);
                              })()}</td>
                              <td>${(() => {
                                const costo = parseFloat(venta.costo || 0);
                                const cantidad = parseFloat(venta.cantidad || 1);
                                const iva = parseFloat(venta.iva || 0) / 100;
                                const totalCostoIva = (costo * cantidad) * (1 + iva);
                                const abono = parseFloat(venta.abono || 0);
                                const adeudado = totalCostoIva - abono;
                                return adeudado.toFixed(2);
                              })()}</td>
                              <td>{
                              venta.fecha 
                                ? new Date(venta.fecha).toLocaleDateString('es-ES', {
                                    timeZone: 'America/Bogota',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  })
                                : '-'
                            }</td>
                              <td>{venta.comprobante || '-'}</td>
                          <td>
                            <div className="buttons">
                              <button
                                className="button is-gold is-small mr-1"
                                onClick={() => handleEditVenta(ventas.indexOf(venta))}
                                title="Editar"
                              >
                                <span className="icon"><i className="fas fa-edit"></i></span>
                              </button>
                              <button
                                className="button is-danger is-small"
                                onClick={() => handleDeleteVenta(ventas.indexOf(venta))}
                                title="Eliminar pago por cobrar"
                              >
                                <span className="icon"><i className="fas fa-trash"></i></span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Tabla de Ventas Totales con scroll */}
          <div style={{
            width: '100%',
            overflowX: 'scroll',
            border: '1px solid #dbdbdb',
            borderRadius: '4px',
            padding: '10px',
            backgroundColor: 'white',
            scrollbarWidth: 'auto',
            WebkitOverflowScrolling: 'touch',
            minHeight: '150px',
            margin: '0 auto'
          }}>
            <table className="table is-bordered is-fullwidth is-striped" style={{
              minWidth: '1200px',
              width: 'max-content',
              marginBottom: '0'
            }}>
            <thead>
              <tr className="has-background-warning has-text-dark">
                <th>Fecha</th>
                <th>Cliente</th>
                <th>CI/RUC</th>
                <th>Producto</th>
                <th>Costo</th>
                <th>Cantidad</th>
                <th>IVA</th>
                <th>Total</th>
                <th>Pagado</th>
                <th>Tipo de Pago</th>
                <th>Banco</th>
                <th># Comprobante</th>
                <th>Tipo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ventasFiltradas = ventas
                  .filter((venta) => venta.tipoPago !== 'porPagar')
                  .filter((venta) => !fechaFiltro || (venta.fecha && esMismoDia(venta.fecha, fechaFiltro)));
                return ventasFiltradas;
              })()
                .map((venta, i) => (
                  <tr key={i}>
                    <td>{
                      venta.fecha
                        ? new Date(venta.fecha).toLocaleDateString('es-ES', {
                            timeZone: 'America/Bogota',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          })
                        : '-'
                    }</td>
                    <td>{venta.cliente || '-'}</td>
                    <td>{venta.ciRuc || '-'}</td>
                    <td>{venta.producto || (venta.tipoPago === 'porPagar' ? 'Pago de Cuenta por Cobrar' : '-')}</td>
                    <td>{venta.tipoPago === 'porPagar' ? `Abono: $${parseFloat(venta.abono || 0).toFixed(2)}` : `$${parseFloat(venta.costo).toFixed(2)}`}</td>
                    <td>{venta.tipoPago === 'porPagar' ? `Pago ${venta.numeroPago || venta.cantidad}` : venta.cantidad}</td>
                    <td>{venta.iva || 0}%</td>
                    <td>${parseFloat(venta.total || 0).toFixed(2)}</td>
                    <td>${parseFloat(venta.pagado).toFixed(2)}</td>
                    <td>
                      <span className={`tag ${venta.tipoPago === 'efectivo' ? 'is-success' : venta.tipoPago === 'transferencia' ? 'is-info' : 'is-warning'}`}>
                        {venta.tipoPago === 'efectivo' ? 'Efectivo' : venta.tipoPago === 'transferencia' ? 'Transferencia' : 'Por Cobrar'}
                      </span>
                    </td>
                    <td>{venta.banco || '-'}</td>
                    <td>{venta.comprobante || '-'}</td>
                    <td>
                      <span className="tag is-success">Venta Manual</span>
                    </td>
                    <td>
                      <div className="buttons">
                        <button
                          className="button is-gold is-small mr-1"
                          onClick={() => handleEditVenta(ventas.indexOf(venta))}
                          title="Editar"
                        >
                          <span className="icon"><i className="fas fa-edit"></i></span>
                        </button>
                        <button
                          className="button is-danger is-small"
                          onClick={() => handleDeleteVenta(ventas.indexOf(venta))}
                          title="Eliminar"
                        >
                          <span className="icon"><i className="fas fa-trash"></i></span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </div>
        {/* Resumen (al final) */}
        <div className="box mt-5" style={{ background: '#eaf3ff' }}>
          <h3 className="subtitle is-6 has-text-centered has-text-link mb-3">
            Resumen {fechaFiltro ? `del Día (${fechaFiltro})` : 'Total'}
          </h3>
          <div className="is-flex is-justify-content-space-around is-flex-wrap-wrap has-text-link">
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Total Pagado: ${calcularTotalDia()}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Efectivo: ${calcularTotalPorTipoPago('efectivo')}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Transferencias: ${calcularTotalPorTipoPago('transferencia')}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Total Adeudado: ${calcularTotalPorTipoPago('porPagar')}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Total Abonado: ${calcularTotalAbonado()}</p>
          </div>
        </div>

        {/* Modal de confirmación para crear en todas las empresas */}
        {showAllEmpresasModal && (
          <div className="modal is-active">
            <div className="modal-background" onClick={() => { setShowAllEmpresasModal(false); pendingVentaRef.current = null; }}></div>
            <div className="modal-card">
              <header className="modal-card-head" style={{ backgroundColor: '#1a1a2e' }}>
                <p className="modal-card-title has-text-warning">⚠️ Confirmar creación para todas las empresas</p>
                <button className="delete" aria-label="close" onClick={() => { setShowAllEmpresasModal(false); pendingVentaRef.current = null; }}></button>
              </header>
              <section className="modal-card-body">
                <div className="content">
                  <p className="has-text-weight-semibold is-size-5">
                    ¿Estás seguro de que deseas crear esta venta para <strong>todas las empresas</strong>?
                  </p>
                  <p>
                    Se creará un registro de esta venta en cada una de las <strong>{empresas.length}</strong> empresas registradas actualmente:
                  </p>
                  <ul>
                    {empresas.map(emp => (
                      <li key={emp.id}>{emp.nombre}</li>
                    ))}
                  </ul>
                  <div className="notification is-warning is-light">
                    <strong>Nota:</strong> Esta acción creará {empresas.length} registros, uno por cada empresa.
                  </div>
                </div>
              </section>
              <footer className="modal-card-foot" style={{ justifyContent: 'flex-end' }}>
                <button className="button" onClick={() => { setShowAllEmpresasModal(false); pendingVentaRef.current = null; }}>
                  No, Cancelar
                </button>
                <button className="button is-warning has-text-dark" onClick={handleCrearParaTodasEmpresas}>
                  Sí, Crear para todas
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
